import { styled } from "goober";
import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useRef,
  useState,
} from "react";
import { useMouseDownOutside } from "../hooks/useMouseDownOutside";
import { useShiftHeld } from "../hooks/useShiftHeld";
import { zIndexes } from "../lib/zIndexes";
import { AnchoredPopover } from "./AnchoredPopover";
import { Button } from "./Button";
import { ClickButton } from "./ClickButton";
import { Icon } from "./Icon";
import { Link } from "./Link";
import { PressButton } from "./PressButton";
import { SearchInput } from "./SearchField";
import { Text } from "./Text";

// Show the search box only once a list is long enough to scan past; shared with
// ListFilter's submenus so the two dropdowns agree on the cutoff.
export const MIN_OPTIONS_FOR_SEARCH = 10;

const Container = styled("div")`
  position: relative;
`;

const Opener = styled(PressButton)`
  display: flex;
  align-items: center;
  gap: 6px;
  width: 100%;
`;

export const ManageDropdownOpener = styled("span")`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 9999px;
  color: var(--text-primary);

  &:hover,
  [aria-expanded="true"] & {
    background: var(--alpha4);
  }

  &:active {
    background: var(--alpha8);
  }
`;

const Popover = styled(AnchoredPopover)`
  background: var(--surface-primary);
  box-shadow: var(--elevation-high);
  outline: 0.5px solid var(--border-alpha);
  z-index: ${zIndexes.dropdown};
  width: 280px;
  max-height: 360px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const SearchContainer = styled("div")`
  display: flex;
  align-items: center;
  height: 36px;
  padding: 0 12px;
  gap: 8px;
  border-bottom: 0.5px solid var(--border-alpha);
  flex-shrink: 0;
`;

const Results = styled("div")`
  flex: 1;
  overflow-y: auto;
  overscroll-behavior: none;
  padding: 4px;
`;

export const SectionLabel = styled("div")`
  padding: 8px 8px 12px;
`;

export const Divider = styled("div")`
  height: 0.5px;
  margin: 12px 8px 4px;
  background: var(--border-alpha);
`;

const SubtleButtonElement = styled(Button)`
  &.secondary {
    color: var(--text-secondary);
  }
`;

// Default to type="button" so these never submit an enclosing modal <form>.
export function SubtleButton(props: React.ComponentProps<typeof Button>) {
  return <SubtleButtonElement type="button" {...props} />;
}

const rowStyles = `
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-radius: 8px;
  cursor: pointer;
  color: var(--text-primary);
  text-decoration: none;
  text-align: left;

  &:is(:hover, :focus-within) {
    background: var(--alpha4);
  }

  &[data-nav-row]:focus-visible {
    outline: none;
    @media (forced-colors: active) {
      outline: 2px solid Highlight;
    }
  }

  & > :is(.row-idle, .row-hover) {
    flex-shrink: 0;
    margin-left: auto;
    margin-right: -4px;
    display: flex;
  }

  & > .row-open {
    flex-shrink: 0;
    margin-left: auto;
    display: none;
  }

  & > .row-hover {
    display: none;
  }

  &:is(:hover, :focus-within) > .row-idle {
    display: none;
  }

  &:is(:hover, :focus-within) > :is(.row-hover, .row-open) {
    display: flex;
  }
`;

export const Row = styled("div")`
  ${rowStyles}
`;

const RowButtonElement = styled(ClickButton)`
  ${rowStyles}
  width: 100%;
`;

export function RowButton(
  props: React.ComponentProps<typeof ClickButton> & {
    ref?: React.Ref<HTMLButtonElement>;
  }
) {
  return <RowButtonElement data-nav-row="" {...props} />;
}

export function DropdownRowContent({
  icon,
  name,
  secondary,
  trailing,
}: {
  icon: React.ReactNode;
  name: React.ReactNode;
  secondary?: React.ReactNode;
  trailing?: React.ReactNode;
}) {
  return (
    <>
      <div
        style={{
          width: 16,
          height: 16,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </div>
      <div style={{ flex: 1, minWidth: 0, textAlign: "left" }}>
        <Text variant="text-regular" color="var(--text-primary)" ellipsis>
          {name}
        </Text>
        {secondary && (
          <Text variant="text-small" color="var(--text-secondary)" ellipsis>
            {secondary}
          </Text>
        )}
      </div>
      {trailing}
    </>
  );
}

const RowLinkElement = styled(Link)`
  ${rowStyles}
  width: 100%;
  box-sizing: border-box;
`;

export function RowLink(props: React.ComponentProps<typeof Link>) {
  return <RowLinkElement data-nav-row="" {...props} />;
}

type Props = {
  opener: React.ReactNode;
  children: (
    query: string,
    close: () => void,
    shiftHeld: boolean
  ) => React.ReactNode;
  searchable?: boolean;
  searchPlaceholder?: string;
  align?: "left" | "right";
  side?: "bottom" | "left" | "right";
  // Gap in px between the opener and the popover (default 4).
  margin?: number;
  height?: number;
  style?: React.CSSProperties;
  disabled?: boolean;
  onOpenChange?: (open: boolean) => void;
  "aria-label"?: string;
  // Receives an imperative open(), for external triggers like keyboard shortcuts.
  openRef?: React.MutableRefObject<(() => void) | null>;
};

function DropdownResults({
  children,
  query,
  close,
  shiftHeld,
  resultsRef,
}: {
  children: Props["children"];
  query: string;
  close: () => void;
  shiftHeld: boolean;
  resultsRef: React.Ref<HTMLDivElement>;
}) {
  return (
    <Results ref={resultsRef}>{children(query, close, shiftHeld)}</Results>
  );
}

export function SearchableDropdown({
  opener,
  children,
  searchable = true,
  searchPlaceholder = "Search…",
  align = "right",
  side = "bottom",
  margin = 4,
  height,
  style,
  disabled,
  onOpenChange,
  "aria-label": ariaLabel,
  openRef,
}: Props) {
  const [isOpen, setIsOpen] = useState(false);
  const [containerRef, portalRef] = useMouseDownOutside(() => setIsOpen(false));
  const [query, setQuery] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const resultsRef = useRef<HTMLDivElement>(null);
  const shiftHeld = useShiftHeld(isOpen);
  const openerId = useId();
  const popoverId = useId();
  const previousOpenRef = useRef(isOpen);

  useEffect(() => {
    if (previousOpenRef.current === isOpen) return;
    previousOpenRef.current = isOpen;
    onOpenChange?.(isOpen);
  }, [isOpen, onOpenChange]);

  const close = useCallback(() => {
    if (portalRef.current?.contains(document.activeElement)) {
      containerRef.current
        ?.querySelector("button")
        ?.focus({ preventScroll: true });
    }
    setIsOpen(false);
  }, [containerRef, portalRef]);

  const open = useCallback(() => {
    if (disabled) return;
    setQuery("");
    setIsOpen(true);
  }, [disabled]);
  useImperativeHandle(openRef, () => open, [open]);

  const toggleOpen = () => {
    if (isOpen) close();
    else open();
  };

  useEffect(() => {
    if (!isOpen) return;
    const popover = portalRef.current;
    if (!popover) return;
    popover.tabIndex = -1;
    const focusFrame = requestAnimationFrame(() =>
      (inputRef.current ?? popover).focus({ preventScroll: true })
    );
    return () => cancelAnimationFrame(focusFrame);
  }, [isOpen, searchable, portalRef]);

  const onKeyDown = (event: React.KeyboardEvent) => {
    if (!isOpen || event.defaultPrevented || event.nativeEvent.isComposing)
      return;
    if (event.key === "Escape") {
      event.preventDefault();
      event.stopPropagation();
      close();
      return;
    }
    if (event.key !== "ArrowDown" && event.key !== "ArrowUp") return;
    const rows = [
      ...(resultsRef.current?.querySelectorAll<HTMLElement>(
        "[data-nav-row]:not(:disabled):not([aria-disabled='true'])"
      ) ?? []),
    ];
    const focusedIndex = rows.indexOf(event.target as HTMLElement);
    const isNavigationRoot =
      event.target === inputRef.current ||
      event.target === portalRef.current ||
      event.target === containerRef.current?.querySelector("button");
    if (!isNavigationRoot && focusedIndex === -1) return;
    if (rows.length === 0) return;
    event.preventDefault();
    event.stopPropagation();
    if (event.key === "ArrowUp" && focusedIndex === 0 && inputRef.current) {
      inputRef.current.focus();
      return;
    }
    const next =
      event.key === "ArrowDown"
        ? (focusedIndex + 1) % rows.length
        : focusedIndex < 0
          ? rows.length - 1
          : (focusedIndex - 1 + rows.length) % rows.length;
    rows[next]?.focus({ preventScroll: true });
    rows[next]?.scrollIntoView({ block: "nearest" });
  };

  return (
    <Container ref={containerRef} style={style} onKeyDown={onKeyDown}>
      <Popover
        id={popoverId}
        role="dialog"
        aria-labelledby={openerId}
        isOpen={isOpen}
        popoverRef={portalRef}
        direction={side === "bottom" ? "down" : side}
        style={
          {
            height,
            ...(height !== undefined
              ? {
                  maxHeight:
                    "min(360px, calc(100dvh / var(--ui-zoom, 1) - 16px))",
                }
              : {}),
            ...(side !== "bottom"
              ? {
                  top: "anchor(top)",
                  ...(side === "left"
                    ? { right: "anchor(left)", marginRight: margin }
                    : { left: "anchor(right)", marginLeft: margin }),
                }
              : {
                  margin,
                  top: "anchor(bottom)",
                  ...(align === "left"
                    ? { left: "anchor(left)" }
                    : { right: "anchor(right)" }),
                }),
            positionTryFallbacks:
              "flip-block, flip-inline, flip-block flip-inline",
          } as React.CSSProperties
        }
        trigger={(props) => (
          <Opener
            {...props}
            id={openerId}
            aria-label={ariaLabel}
            aria-haspopup="dialog"
            aria-expanded={isOpen}
            aria-controls={isOpen ? popoverId : undefined}
            disabled={disabled}
            onPress={toggleOpen}
          >
            {opener}
          </Opener>
        )}
      >
        {searchable && (
          <SearchContainer>
            <Icon name="search" size={12} />
            <SearchInput
              ref={inputRef}
              type="text"
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
          </SearchContainer>
        )}
        {isOpen && (
          <DropdownResults
            query={query}
            close={close}
            shiftHeld={shiftHeld}
            resultsRef={resultsRef}
          >
            {children}
          </DropdownResults>
        )}
      </Popover>
    </Container>
  );
}
